// Auth API functions and React Query hooks
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiRequest, ApiError, API_CONFIG } from './config'
import { type UserProfileResponse } from '../types/auth'

// Query keys for React Query
export const AUTH_QUERY_KEYS = {
  me: ['auth', 'me'] as const,
} as const

export interface SignInParams {
  email: string
  password: string
  rememberMe?: boolean
}

export interface SignInResponse {
  message?: string
}

export interface SignOutResponse {
  message?: string
}

/**
 * Hook to get current user profile information
 * Uses React Query for caching and automatic refetching
 * Calls /api/user/profile endpoint
 */
export const useMe = (): UseQueryResult<UserProfileResponse, ApiError> => {
  return useQuery({
    queryKey: AUTH_QUERY_KEYS.me,
    queryFn: async (): Promise<UserProfileResponse> => {
      return apiRequest<UserProfileResponse>('/api/user/profile')
    },
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false
      }
      return failureCount < 2
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Manual function to fetch user profile (without React Query)
 * Useful for server-side or one-off requests
 */
export const fetchUserProfile = async (): Promise<UserProfileResponse> => {
  return apiRequest<UserProfileResponse>('/api/user/profile')
}

/**
 * Sign in with email and password
 */
export const signIn = async (params: SignInParams): Promise<SignInResponse> => {
  return apiRequest<SignInResponse>(API_CONFIG.ENDPOINTS.AUTH.SIGNIN, {
    method: 'POST',
    body: JSON.stringify({
      email: params.email,
      password: params.password,
      rememberMe: params.rememberMe ?? true,
    }),
  })
}

/**
 * Initiate Google OAuth sign-in flow
 * @param redirect Optional redirect URL after successful authentication
 * @param data Optional data to be sent along with the request
 * @returns URL to redirect user to for Google OAuth
 */
export const initiateGoogleOAuth = async (redirect?: string, data?: unknown): Promise<{ url: string }> => {
  let endpoint = `/api/auth/google/start`;
  const params: string[] = [];
  if (redirect) params.push(`redirect=${encodeURIComponent(redirect)}`);
  if (data) {
    try {
      const json = JSON.stringify(data);
      if (json.length <= 2000) params.push(`data=${encodeURIComponent(json)}`);
    } catch { /* ignore */ }
  }
  if (params.length) endpoint += `?${params.join('&')}`;
  return apiRequest<{ url: string }>(endpoint)
}

/**
 * Sign out the current user
 */
export const signOut = async (): Promise<SignOutResponse> => {
  return apiRequest<SignOutResponse>(API_CONFIG.ENDPOINTS.AUTH.SIGNOUT, {
    method: 'POST',
  })
}

/**
 * Mark onboarding as completed
 */
export const completeOnboarding = async (): Promise<void> => {
  await apiRequest('/api/user/onboarding/complete', { method: 'POST' })
}

/**
 * Verify email address with token
 */
export const verifyEmail = async (email: string, token: string): Promise<{ message: string }> => {
  return apiRequest<{ message: string }>('/api/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ email, token }),
  })
}

/**
 * Resend email verification
 */
export const resendEmailVerification = async (email: string): Promise<{ message: string }> => {
  return apiRequest<{ message: string }>('/api/auth/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}
